"use client"

import type { ReactNode } from "react"
import { motion, useReducedMotion } from "motion/react"

import { cn } from "@/lib/utils"

const EASE_OUT: [number, number, number, number] = [0, 0, 0.2, 1]

interface RevealProps {
  children: ReactNode
  className?: string
  /** Stagger delay in seconds (design uses d1=0.08 … d4=0.32). */
  delay?: number
  /** "in-view" (default) fades up when scrolled into view; "mount" animates
   * immediately on load — used for the above-the-fold hero. */
  mode?: "in-view" | "mount"
}

/**
 * Scroll-reveal wrapper that mirrors the prototype's `.reveal` keyframe
 * (fade + 22px rise). Uses the same `motion/react` idiom as the homepage hero.
 * Honors `prefers-reduced-motion` by rendering static, fully-visible content.
 */
export function Reveal({ children, className, delay = 0, mode = "in-view" }: RevealProps) {
  const reduceMotion = useReducedMotion()

  // Always render motion.div, even under reduced motion. The server cannot see
  // the preference and emits the hidden initial style; swapping to a plain
  // <div> on the client keeps that inline opacity:0 through hydration and the
  // content never appears. Reduced motion instead shows everything at once.
  const animateProps =
    mode === "mount" || reduceMotion
      ? { animate: { opacity: 1, y: 0 } }
      : {
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.1, margin: "0px 0px -6% 0px" },
        }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 22 }}
      transition={reduceMotion ? { duration: 0 } : { duration: 0.7, ease: EASE_OUT, delay }}
      {...animateProps}
    >
      {children}
    </motion.div>
  )
}

interface FloatingChipProps {
  children: ReactNode
  className?: string
  /** Phase offset so the three hero chips don't bob in unison. */
  delay?: number
}

/**
 * Decorative floating callout chip over the hero phone. Bobs gently on an
 * infinite loop (prototype `@keyframes floaty`), static under reduced motion.
 */
export function FloatingChip({ children, className, delay = 0 }: FloatingChipProps) {
  const reduceMotion = useReducedMotion()

  return (
    <motion.div
      className={className}
      animate={reduceMotion ? { y: 0 } : { y: [0, -9, 0] }}
      transition={
        reduceMotion ? { duration: 0 } : { duration: 5, repeat: Infinity, ease: "easeInOut", delay }
      }
    >
      {children}
    </motion.div>
  )
}

/** Shared chip inner layout (icon tile + label/value) used by all three chips. */
export function ChipBody({
  tone,
  icon,
  label,
  value,
}: {
  tone: "green" | "amber" | "blue"
  icon: ReactNode
  label: string
  value: string
}) {
  const toneClasses =
    tone === "green"
      ? "bg-green-50 text-green-600"
      : tone === "amber"
        ? "bg-amber-50 text-amber-600"
        : "bg-blue-50 text-blue-600"

  return (
    <div className="flex items-center gap-[11px] rounded-2xl border border-white/90 bg-white/90 px-[15px] py-3 shadow-[0_16px_36px_-12px_rgba(16,40,24,0.30)] backdrop-blur-sm">
      <span className={cn("grid h-[38px] w-[38px] shrink-0 place-items-center rounded-[11px]", toneClasses)}>
        {icon}
      </span>
      <span className="leading-tight">
        <span className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500">{label}</span>
        <span className="block font-poppins text-base font-extrabold leading-[1.1] text-gray-900">{value}</span>
      </span>
    </div>
  )
}
